import * as React from "react";
import PropTypes from "prop-types";
import { cn } from "../lib/utils.js";
import { Button } from "./button.jsx";
import { FastTooltip } from "./FastTooltip.jsx";
import { Slottable } from "@radix-ui/react-slot";

/**
 * CircularActionButton: Canonical 36px circular icon button.
 * Enforces Inbox cockpit aesthetic, 20px icon, 100ms fast tooltip,
 * and a 44px mobile touch envelope.
 */
export const CircularActionButton = React.forwardRef(
  (
    {
      icon: Icon,
      label,
      tooltip,
      onClick,
      variant = "outline",
      isPrimary = false,
      colorClass,
      disabled = false,
      className,
      tooltipSide = "bottom",
      delayDuration = 100,
      asChild = false,
      children,
      "data-testid": testId,
      dataTestId,
      "aria-label": ariaLabelProp,
      ...props
    },
    ref,
  ) => {
    const isPrimaryAction = Boolean(
      isPrimary ||
        variant === "default" ||
        variant === "solid" ||
        variant === "primary",
    );
    const resolvedVariant = isPrimaryAction
      ? "solid"
      : variant === "default"
        ? "outline"
        : variant;
    const resolvedTestId = testId || dataTestId;
    const computedAriaLabel =
      ariaLabelProp !== undefined
        ? ariaLabelProp
        : typeof label === "string"
          ? label
          : undefined;

    const buttonContent = (
      <Button
        ref={ref}
        type={asChild ? undefined : "button"}
        variant={resolvedVariant}
        size="icon"
        asChild={asChild}
        disabled={disabled}
        onClick={onClick}
        aria-label={computedAriaLabel}
        data-testid={resolvedTestId}
        className={cn(
          "relative h-9 w-9 shrink-0 rounded-full p-0 [&_svg]:size-5 transition-all duration-150 active:scale-[0.98] motion-reduce:transform-none touch-manipulation select-none cursor-pointer",
          // 44px mobile touch envelope without altering visual 36px size
          "before:absolute before:-inset-1 sm:before:inset-0 before:content-['']",
          // Outline style uses solid bg-background to match Inbox CockpitQuickActions
          !isPrimaryAction &&
            resolvedVariant === "outline" &&
            "bg-background border border-border text-foreground hover:border-primary/40 hover:bg-muted/80 hover:text-foreground hover:shadow-xs",
          // Solid primary styling for create CTAs
          isPrimaryAction &&
            "bg-primary text-primary-foreground hover:bg-primary/90 hover:border-primary/40 hover:shadow-sm border border-primary/25 shadow-xs",
          className,
        )}
        {...props}
      >
        {Icon && (
          <Icon
            size={20}
            color={isPrimaryAction ? "currentColor" : undefined}
            className={cn(
              "h-5 w-5 shrink-0 transition-colors",
              !isPrimaryAction && colorClass,
            )}
            aria-hidden="true"
          />
        )}
        {asChild ? <Slottable>{children}</Slottable> : children}
        {label && <span className="sr-only">{label}</span>}
      </Button>
    );

    const resolvedTooltip = tooltip || label;

    return (
      <FastTooltip
        content={resolvedTooltip}
        side={tooltipSide}
        delayDuration={delayDuration}
        disabled={disabled || !resolvedTooltip}
      >
        {buttonContent}
      </FastTooltip>
    );
  },
);

CircularActionButton.displayName = "CircularActionButton";

CircularActionButton.propTypes = {
  icon: PropTypes.elementType,
  label: PropTypes.node,
  tooltip: PropTypes.node,
  onClick: PropTypes.func,
  variant: PropTypes.oneOf([
    "default",
    "solid",
    "primary",
    "outline",
    "ghost",
    "secondary",
    "destructive",
  ]),
  isPrimary: PropTypes.bool,
  colorClass: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  tooltipSide: PropTypes.oneOf(["top", "bottom", "left", "right"]),
  delayDuration: PropTypes.number,
  asChild: PropTypes.bool,
  children: PropTypes.node,
  "data-testid": PropTypes.string,
  dataTestId: PropTypes.string,
  "aria-label": PropTypes.string,
};

export default CircularActionButton;
